import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiSparkles, HiXMark } from 'react-icons/hi2';

interface Tip {
  id: number;
  text: string;
  icon: string;
}

interface TipsSectionProps {
  hide?: boolean;
}

const tips: Tip[] = [
  {
    id: 1,
    text: "First search didn't get the best results? Try the same prompt again - AI improves with each attempt!",
    icon: "🔄"
  },
  {
    id: 2,
    text: "Be specific! Instead of 'gaming laptop', try 'laptop for gaming with RTX 4060 under $1200'",
    icon: "🎯"
  },
  {
    id: 3,
    text: "Mention your use case: 'laptop for video editing', 'coding and programming', or 'student use'",
    icon: "💡"
  },
  {
    id: 4,
    text: "Include your budget range for better recommendations: 'best laptop under $800' or 'premium laptops around $2000'",
    icon: "💰"
  },
  {
    id: 5,
    text: "Compare brands directly: 'MacBook Pro vs Dell XPS 15' or 'Lenovo ThinkPad vs HP EliteBook'",
    icon: "⚡"
  },
  {
    id: 6,
    text: "Specify screen size or portability: '13-inch ultrabook', '17-inch workstation', or 'lightweight for travel'",
    icon: "📏"
  },
];

export const TipsSection: React.FC<TipsSectionProps> = ({ hide = false }) => {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if user has dismissed tips before
    const dismissed = localStorage.getItem('tipsSection_dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
      setIsVisible(false);
      return;
    }

    // Rotate tips every 6 seconds
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % tips.length);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  // Hide when search is active
  if (hide) {
    return null;
  }

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      setIsDismissed(true);
      localStorage.setItem('tipsSection_dismissed', 'true');
    }, 300);
  };

  const handleShowAgain = () => {
    setIsDismissed(false);
    setIsVisible(true);
    localStorage.removeItem('tipsSection_dismissed');
  };

  if (isDismissed) {
    return (
      <motion.button
        onClick={handleShowAgain}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[var(--brand)] to-[var(--accent)] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <HiSparkles size={18} />
        <span className="text-sm font-semibold">Show Tips</span>
      </motion.button>
    );
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="w-full mb-6 sm:mb-8"
          initial={{ opacity: 0, y: -20, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          exit={{ opacity: 0, y: -20, height: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[var(--brand-lighter)] via-white to-[var(--accent-light)] border border-[var(--brand)]/20 shadow-md">
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-[var(--brand)]/5 via-transparent to-[var(--accent)]/5 animate-gradient" />
              
              <div className="relative px-4 sm:px-6 py-3 sm:py-4">
                <div className="flex items-start gap-3 sm:gap-4">
                  {/* Icon */}
                  <motion.div
                    className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[var(--brand)] to-[var(--accent)] rounded-xl flex items-center justify-center shadow-md text-white"
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 4 }}
                  >
                    <HiSparkles size={24} />
                  </motion.div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="text-sm sm:text-base font-bold text-[var(--brand)]">
                        Pro Tip
                      </h2>
                      <div className="hidden sm:flex gap-1">
                        {tips.map((_, index) => (
                          <motion.div
                            key={index}
                            className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
                              index === currentTipIndex
                                ? 'bg-[var(--brand)]'
                                : 'bg-[var(--surface-4)]'
                            }`}
                            animate={
                              index === currentTipIndex
                                ? { scale: [1, 1.3, 1] }
                                : { scale: 1 }
                            }
                            transition={{ duration: 0.5 }}
                          />
                        ))}
                      </div>
                    </div>
                    
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentTipIndex}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="flex items-start gap-2"
                      >
                        <span className="text-2xl flex-shrink-0 mt-0.5">
                          {tips[currentTipIndex].icon}
                        </span>
                        <p className="text-sm sm:text-base text-[var(--text-2)] leading-relaxed">
                          {tips[currentTipIndex].text}
                        </p>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Close Button */}
                  <motion.button
                    onClick={handleDismiss}
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-[var(--text-3)] hover:text-[var(--error)] hover:bg-[var(--error-bg)] transition-colors duration-200"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Dismiss tips"
                  >
                    <HiXMark size={20} />
                  </motion.button>
                </div>
              </div>

              {/* Progress bar */}
              <motion.div
                className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[var(--brand)] to-[var(--accent)] rounded-full"
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 6, ease: 'linear', repeat: Infinity }}
                key={currentTipIndex}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
